import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RelationshipType } from '@common/entities';
import { useQuery } from '@tanstack/react-query';
import { CUSTOMER_RELATIONSHIPS } from 'mocks/leasing-journey-api-responses';
import { RootState } from 'state/store';

import { getCustomerRelationships } from 'services';
import {
  getRequiresContactInfo,
  isAdultCustomerType,
  isNonLeasingOccupantCustomerType,
} from 'utils/leadProfileUtils';

const DEFAULT_RELATIONSHIP_TYPE = RelationshipType.Primary;

export const useGetApplicantRelationship = () => {
  const isLeadProfile = useSelector((state: RootState) => state.auth?.isLeadProfile ?? false);
  const isPreviewMode = useSelector((state: RootState) => state.auth?.isPreviewMode ?? false);
  const applicants = useSelector((state: RootState) => state.leadProfile?.applicants ?? []);
  const applicantResponses = useSelector(
    (state: RootState) => state.leadProfile?.applicantResponses ?? {},
  );
  const responseId = useSelector(
    (state: RootState) => state.navigation?.leadProfileResponseId ?? '',
  );
  const activeResponse = useSelector((state: RootState) => state.application.activeResponse);

  const { data: customerRelationships } = useQuery({
    queryKey: ['customer-relationships'],
    queryFn: getCustomerRelationships,
    enabled: !isPreviewMode,
  });

  const getApplicantRelationship = useMemo(
    () => (): RelationshipType => {
      if (isLeadProfile) {
        const matchedApplicant = applicants.find((app) => app.responseId === responseId);
        return (
          (matchedApplicant?.relationshipType as RelationshipType) ?? DEFAULT_RELATIONSHIP_TYPE
        );
      }
      return (activeResponse?.relationshipType as RelationshipType) ?? DEFAULT_RELATIONSHIP_TYPE;
    },
    [isLeadProfile, applicants, responseId, activeResponse],
  );

  // Resolves the Entrata customer relationship id for the active applicant, falling
  // back to the primary's coApplicantList when the applicant has none recorded yet.
  const resolveRelationshipId = useMemo(
    () => (): number | string | undefined => {
      const matchedApplicant = applicants.find((app) => app.responseId === responseId);
      if (!matchedApplicant) return undefined;

      const relationshipId = matchedApplicant.entrataCustomerRelationshipId;
      if (relationshipId) return relationshipId;

      const primaryResponseId = applicants.find(
        (a) => a.relationshipType === RelationshipType.Primary,
      )?.responseId;
      const primaryResponseData = primaryResponseId ? applicantResponses[primaryResponseId] : null;
      const coApplicantList = primaryResponseData?.coApplicant?.coApplicantList || [];
      const guarantorList = primaryResponseData?.guarantor?.guarantorList || [];

      const coApp = coApplicantList.find((c: any) => c.responseId === responseId);
      if (coApp?.relationship) return Number(coApp.relationship);

      const guarantor = guarantorList.find((g: any) => g.responseId === responseId);
      if (guarantor?.relationship) return Number(guarantor.relationship);

      return undefined;
    },
    [applicants, responseId, applicantResponses],
  );

  // Resolves the Entrata customer_type_id for the active applicant from the relationship list.
  const resolveCustomerTypeId = useMemo(
    () => (): string | undefined => {
      const relationshipId = resolveRelationshipId();
      if (!relationshipId) return undefined;

      const dataToFilter = isPreviewMode ? CUSTOMER_RELATIONSHIPS : customerRelationships?.data;
      const relationship = dataToFilter?.find((r: any) => Number(r.id) === Number(relationshipId));
      return relationship?.customer_type_id?.toString();
    },
    [resolveRelationshipId, isPreviewMode, customerRelationships],
  );

  const getApplicantRequiresContactInfo = useMemo(
    () =>
      (isAdult: boolean = true): boolean => {
        if (!isAdult) return false;

        // Non-Leasing Occupants always need a phone number (email stays optional),
        // so they require contact info even though they map to the 'occupant' type.
        if (isNonLeasingOccupantCustomerType(resolveCustomerTypeId())) return true;

        // If not in lead profile, we assume contact info is required (they are filling their own app)
        if (!isLeadProfile) return true;

        const relationshipId = resolveRelationshipId();
        if (!relationshipId) return true;

        const dataToFilter = isPreviewMode ? CUSTOMER_RELATIONSHIPS : customerRelationships?.data;
        return getRequiresContactInfo(relationshipId, dataToFilter, isAdult);
      },
    [
      isLeadProfile,
      resolveRelationshipId,
      resolveCustomerTypeId,
      isPreviewMode,
      customerRelationships,
    ],
  );

  // Email requiredness follows contact-info requiredness, except Non-Leasing Occupants
  // for whom email is optional (they still require a phone number).
  const getApplicantRequiresEmail = useMemo(
    () =>
      (isAdult: boolean = true): boolean => {
        if (!getApplicantRequiresContactInfo(isAdult)) return false;
        return !isNonLeasingOccupantCustomerType(resolveCustomerTypeId());
      },
    [getApplicantRequiresContactInfo, resolveCustomerTypeId],
  );

  // Adult/minor status from customer type (not a computed age). Outside the lead profile
  // applicants fill their own app, so they're treated as adults.
  const getApplicantIsAdult = useMemo(
    () => (): boolean => {
      if (!isLeadProfile) return true;
      return isAdultCustomerType(resolveCustomerTypeId());
    },
    [isLeadProfile, resolveCustomerTypeId],
  );

  return {
    getApplicantRelationship,
    getApplicantRequiresContactInfo,
    getApplicantRequiresEmail,
    getApplicantIsAdult,
  };
};
